import {
  Guest,
  Property,
  Reservation,
  ReservationResponse,
} from '@data/models';
import { apiCall, IRequestOptions } from '../axios';
import Payment from '@data/models/Payment';
import ReservationItem, {
  ReservationItemAccommodation,
} from '@data/models/ReservationItem';
import TimeUtils from '@business/utils/time';
import { getCreditCardType } from '@business/utils/creditcard';

export class ReservationService {
  static getReservation(
    reservationCode: string,
    options: IRequestOptions = {}
  ) {
    const url = '/confirmation?data_res=' + reservationCode;
    return apiCall(Reservation)('get', url, {}, options);
  }

  static makeReservation(
    property: Property,
    items: ReservationItem[],
    checkin: Date,
    checkout: Date,
    guests: Guest[],
    payment: Payment
  ) {
    const data = {
      // property info
      widget_property: property.id,
      association_id: 0,
      lang: 'en',

      // reservation info
      selected_checkin: TimeUtils.format(checkin),
      selected_checkout: TimeUtils.format(checkout),
      rooms: this.stringifyAccomodations(items),

      agree: 1,

      //selectedLinks: {},
      //allotment_block_code:
      //source_code:
      //csrf_accessa:c9f4df8503b7b56674f2af74d5aee980
    };

    // guest info ------------------------------
    const mainGuest = guests[0];
    data['first_name'] = mainGuest.firstName;
    data['last_name'] = mainGuest.lastName;
    data['email'] = mainGuest.email;

    // require phone
    data['phone'] = mainGuest.phone;

    // ask gender
    if (property.settings.askGender) {
      data['gender'] = mainGuest.gender;
    }

    // ask nationality
    if (property.settings.askNationality) {
      data['country'] = mainGuest.country;
    }

    // ask postal address
    if (property.settings.askPostalAddress) {
      data['zip'] = mainGuest.zipCode;
      data['address1'] = mainGuest.address1;
      data['address2'] = mainGuest.address2;
      data['city'] = mainGuest.city;
      data['state'] = mainGuest.state;
    }

    // ask estimated arrival time --------------
    if (property.settings.askEstimatedArrivalTime) {
      data['booking_estimated_arrival_time'] = mainGuest.estimatedArrivalTime;
    }

    // ask tax id ------------------------------
    if (property.settings.askGuestTaxID) {
      data['guest_tax_id_number'] = mainGuest.taxIdNumber;
    }

    // ask company name ------------------------
    if (property.settings.askCompanyName) {
      data['company_name'] = mainGuest.companyName;
    }

    // ask company tax id ---------------------
    if (property.settings.askCompanyTaxID) {
      data['company_tax_id_number'] = mainGuest.companyTaxIdNumber;
    }

    // custom fields ---------------------------
    // TODO: implement custom fields
    // data['cf_custom_food'] = mainGuest.customFood;

    // payment info ----------------------------
    data['payment_gateway'] = payment.gateway;
    data['payment_method'] = payment.method;

    if (payment.method === 'cards' && payment.card) {
      const dateParts = payment.card['expiration-date'].split('/');
      data['card_number'] = payment.card['credit-card'].replace(/\D/g, ''); // remove non numeric characters
      data['cardholder_name'] = payment.card['cardholder'];
      data['exp_month'] = dateParts[0];
      data['exp_year'] = 2000 + parseInt(dateParts[1]);
      data['choose_card'] = getCreditCardType(payment.card['credit-card']);
      // the field is called cvv in the backend
      data['cvv'] = payment.card.cvc;
    }

    return apiCall(ReservationResponse)('post', '/prepare', data);
  }

  static stringifyAccomodations(items: ReservationItem[], format = false) {
    const rooms = {};

    const privateAccomodations = items
      .filter(
        ({ model }) =>
          model?.type === 'accommodation' &&
          !model?.item?.sourceSettings.canSelectSpecificRoom
      )
      .map((x) => x.model as ReservationItemAccommodation);

    const sharedAccomodations = items
      .filter(
        ({ model }) =>
          model?.type === 'accommodation' &&
          model?.item?.sourceSettings.canSelectSpecificRoom
      )
      .map((x) => x.model as ReservationItemAccommodation);

    for (let i = 0; i < privateAccomodations.length; i++) {
      const item = privateAccomodations[i];
      const rateId = item.item.rateId.toString();
      rooms[rateId] = [];
      for (let i = 0; i < item.amount; i++) {
        rooms[rateId].push({
          adults: item.adults.toString(),
          kids: item.kids.toString(),
        });
      }
    }

    for (let i = 0; i < sharedAccomodations.length; i++) {
      const item = sharedAccomodations[i];
      const rateId = item.item.rateId.toString();
      if (!rooms[rateId]) rooms[rateId] = {};
      const key = item.unit as string;
      rooms[rateId][key] = {
        adults: item.adults.toString(),
        kids: item.kids.toString(),
      };
    }

    return JSON.stringify(rooms, null, format ? 2 : undefined);
  }
}
